import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import ConfirmDialog from "../../components/ConfirmDialog";
import useConfirmDialog from "../../hooks/useConfirmDialog";

const TARGET_OPTIONS = [
  { label: "All customers", value: "all" },
  { label: "Specific company", value: "company" },
  { label: "Specific device", value: "device" },
];

export default function OpsAnnouncements() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [announcements, setAnnouncements] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({
    title: "",
    body: "",
    targetType: "all",
    companyId: "",
    deviceFingerprint: "",
    publishAt: "",
    linkUrl: "",
    isActive: true,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { confirmProps, openConfirm } = useConfirmDialog();

  const fetchData = async () => {
    try {
      setLoading(true);
      const [announcementsRes, companiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/ops/announcements`, { headers }),
        axios.get(`${API_BASE_URL}/ops/companies`, { headers }),
      ]);
      setAnnouncements(announcementsRes.data);
      setCompanies(companiesRes.data);
      setError("");
    } catch (err) {
      console.error("OpsAnnouncements fetch error", err.response?.data || err.message);
      setError("Unable to load announcements.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      await axios.post(
        `${API_BASE_URL}/ops/announcements`,
        {
          title: form.title,
          body: form.body,
          targetType: form.targetType,
          companyId: form.targetType === "company" ? form.companyId : undefined,
          deviceFingerprint: form.targetType === "device" ? form.deviceFingerprint.trim() : undefined,
          publishAt: form.publishAt || undefined,
          linkUrl: form.linkUrl || undefined,
          isActive: form.isActive,
        },
        { headers }
      );
      setSuccess("Announcement created.");
      setForm({
        title: "",
        body: "",
        targetType: "all",
        companyId: "",
        deviceFingerprint: "",
        publishAt: "",
        linkUrl: "",
        isActive: true,
      });
      fetchData();
    } catch (err) {
      console.error("OpsAnnouncements create error", err.response?.data || err.message);
      setError(err.response?.data?.error || "Unable to create announcement.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/ops/announcements/${id}`, { headers });
      setAnnouncements((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      console.error("OpsAnnouncements delete error", err.response?.data || err.message);
      setError("Unable to delete announcement.");
    }
  };

  const confirmDelete = (announcement) => {
    openConfirm({
      title: "Delete Announcement",
      message: `Delete "${announcement.title}"? This cannot be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
      onConfirm: () => handleDelete(announcement.id),
    });
  };

  return (
    <div className="ops-section">
      <div className="ops-section-header">
        <h3>Create Announcement</h3>
      </div>
      <form onSubmit={handleSubmit} className="ops-form">
        {error && <p className="text-danger">{error}</p>}
        {success && <p className="text-success">{success}</p>}

        <div className="mb-3">
          <label className="form-label">Title</label>
          <input
            type="text"
            className="form-control"
            name="title"
            value={form.title}
            onChange={handleChange}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Body</label>
          <textarea
            className="form-control"
            name="body"
            rows={3}
            value={form.body}
            onChange={handleChange}
            required
          />
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Target</label>
            <select
              name="targetType"
              className="form-select"
              value={form.targetType}
              onChange={handleChange}
            >
              {TARGET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {form.targetType === "company" && (
            <div className="col-md-4 mb-3">
              <label className="form-label">Company</label>
              <select
                name="companyId"
                className="form-select"
                value={form.companyId}
                onChange={handleChange}
                required
              >
                <option value="">Select company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.targetType === "device" && (
            <div className="col-md-4 mb-3">
              <label className="form-label">Device Fingerprint</label>
              <input
                type="text"
                className="form-control"
                name="deviceFingerprint"
                value={form.deviceFingerprint}
                onChange={handleChange}
                required
              />
            </div>
          )}

          <div className="col-md-4 mb-3">
            <label className="form-label">Publish At</label>
            <input
              type="datetime-local"
              className="form-control"
              name="publishAt"
              value={form.publishAt}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Link URL (optional)</label>
            <input
              type="url"
              className="form-control"
              name="linkUrl"
              value={form.linkUrl}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>
          <div className="col-md-6 mb-3 d-flex align-items-end">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="isActive"
                id="opsAnnouncementActive"
                checked={form.isActive}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="opsAnnouncementActive">
                Active immediately
              </label>
            </div>
          </div>
        </div>

        <button className="btn btn-primary" type="submit" disabled={submitting}>
          {submitting ? "Publishing..." : "Publish Announcement"}
        </button>
      </form>

      <div className="ops-section" style={{ marginTop: 24 }}>
        <div className="ops-section-header">
          <h3>Recent Announcements</h3>
        </div>
        {loading ? (
          <p>Loading announcements...</p>
        ) : (
          <div className="ops-table-wrapper">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Target</th>
                  <th>Publish At</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {announcements.map((announcement) => (
                  <tr key={announcement.id}>
                    <td>{announcement.title}</td>
                    <td>
                      {announcement.audience === "all"
                        ? "All"
                        : announcement.audience === "company"
                        ? `Company (${announcement.company_id || "n/a"})`
                        : `Device (${announcement.device_fingerprint || "n/a"})`}
                    </td>
                    <td>{announcement.publish_at ? new Date(announcement.publish_at).toLocaleString() : "—"}</td>
                    <td>{announcement.is_active ? "Active" : "Paused"}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => confirmDelete(announcement)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {announcements.length === 0 && (
                  <tr>
                    <td colSpan={5}>No announcements yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmDialog {...confirmProps} />
    </div>
  );
}
